import { z } from "zod";

export const FEATURE_FLAG_GROUPS = [
  "developers",
  "test-group",
  "all-users",
] as const;

export const featureFlagGroupsSchema = z.array(z.enum(FEATURE_FLAG_GROUPS));

const featureFlagSchema = z.object({
  enabled: z.boolean().default(false),
  groups: featureFlagGroupsSchema.default(["developers", "test-group"]),
});

export const appConfigSchemaStrict = z.object({
  roleFromEntraGroup: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  sharepointDownloadUrl: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  driverOldFidoHrefOverride: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  resources_cancelledTrains: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  trainInsights: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  evaluationForm: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  topasKjoretoyPwaLink: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  kotlinBFF: featureFlagSchema.default({
    enabled: true,
    groups: ["developers"],
  }),
  syncAppConfig: featureFlagSchema
    .and(
      z.object({
        intervalMs: z.number(),
      }),
    )
    .default({
      enabled: true,
      groups: ["all-users"],
      intervalMs: 60_000,
    }),
});

export const appConfigSchema = z.preprocess(
  (possiblyAppConfig) => {
    const transformedPossiblyAppConfig = !possiblyAppConfig
      ? {}
      : typeof possiblyAppConfig === "string" && possiblyAppConfig.length > 0
        ? JSON.parse(possiblyAppConfig)
        : possiblyAppConfig;

    // Keep only top-level entries whose values look like feature flags;
    // with .passthrough(), unknown feature-flag-like keys are preserved too.
    return Object.fromEntries(
      Object.entries(transformedPossiblyAppConfig).filter(([_, value]) =>
        isFeatureFlag(value),
      ),
    );
  },
  appConfigSchemaStrict
    // Passthrough allows deprecated feature flags to remain in config
    // until clients update, ensuring backward compatibility during rollouts.
    .passthrough(),
);

export type AppConfig = z.infer<typeof appConfigSchemaStrict>;

type FeatureFlag = z.infer<typeof featureFlagSchema>;
export type FeatureFlagGroup = FeatureFlag["groups"][number];

type IsFeatureFlag<T> = T extends FeatureFlag ? T : never;

/** Extract all properties that confirms to featureFlagSchema.
 * For example: { a: { enabled: true, groups: []}, c: 1 } } => "a"
 * as c does not conform to the feature flag schema
 **/
type PathsToFeatureFlags<T extends object> = {
  [K in keyof T]: IsFeatureFlag<T[K]> extends never ? never : K;
}[keyof T];

// Our final FeatureFlagPath type that represents all possible keys of AppConfig, including nested ones
export type FeatureFlagPath = PathsToFeatureFlags<AppConfig>;

export function isFeatureFlag(obj: unknown): obj is FeatureFlag {
  return (
    obj !== null &&
    typeof obj === "object" &&
    "enabled" in obj &&
    "groups" in obj
  );
}

export const isFeatureFlagPath = (
  path: string,
  appConfig: AppConfig,
): path is FeatureFlagPath => {
  const featureFlags = Object.entries(appConfig).filter(([_, value]) =>
    isFeatureFlag(value),
  );

  return path in Object.fromEntries(featureFlags);
};
